import { css } from '@emotion/react';
import {
  Anchor,
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  Modal,
  Stack,
  Text,
} from '@inflearn/ds-react-next';
import { useEffect, useState } from 'react';

const PRIVACY_POLICY_URL = 'https://www.inflearn.com/policy/privacy';

interface MockInterviewConsentModalProps {
  opened: boolean;
  onClose: () => void;
  onAgree: () => void;
}

export function MockInterviewConsentModal({
  opened,
  onClose,
  onAgree,
}: MockInterviewConsentModalProps) {
  const [agreedPrivacy, setAgreedPrivacy] = useState(false);
  const [agreedMedia, setAgreedMedia] = useState(false);

  // 매번 노출 정책: 모달이 열리거나 닫힐 때마다 체크 상태를 초기화한다.
  useEffect(() => {
    setAgreedPrivacy(false);
    setAgreedMedia(false);
  }, [opened]);

  const allAgreed = agreedPrivacy && agreedMedia;

  const handleAllChange = (checked: boolean) => {
    setAgreedPrivacy(checked);
    setAgreedMedia(checked);
  };

  return (
    <Modal.Root opened={opened} onClose={onClose} size="md" radius={16}>
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>모의면접 참여 동의</Modal.Title>
          <Modal.CloseButton />
        </Modal.Header>
        <Modal.Body>
          <Stack gap={8}>
            <Text fz={16} fw={500} c="dark.9">
              원활한 서비스 제공을 위해 아래 항목에 동의가 필요해요
            </Text>
            <Box p={20} css={styles.consentBox}>
              <Stack gap={16}>
                <Flex align="center" justify="space-between">
                  <Checkbox
                    checked={allAgreed}
                    onChange={(e) => handleAllChange(e.currentTarget.checked)}
                    label={<Text c="dark.9">전체 동의</Text>}
                    variant="filled"
                    color="infgreen"
                    size="md"
                    labelPosition="right"
                  />
                  <Anchor
                    href={PRIVACY_POLICY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    fz={16}
                    c="gray.7"
                    underline="always"
                  >
                    약관 보기
                  </Anchor>
                </Flex>
                <Divider orientation="horizontal" variant="solid" size="xs" />
                <Checkbox
                  checked={agreedPrivacy}
                  onChange={(e) => setAgreedPrivacy(e.currentTarget.checked)}
                  label={
                    <Text c="dark.9">(필수) 이력서 ∙ 경력기술서 ∙ 포트폴리오 수집 ∙ 이용 동의</Text>
                  }
                  variant="filled"
                  color="infgreen"
                  size="md"
                  labelPosition="right"
                />
                <Checkbox
                  checked={agreedMedia}
                  onChange={(e) => setAgreedMedia(e.currentTarget.checked)}
                  label={<Text c="dark.9">(필수) 영상 ∙ 음성 정보 수집 및 AI 분석 이용 동의</Text>}
                  variant="filled"
                  color="infgreen"
                  size="md"
                  labelPosition="right"
                />
              </Stack>
            </Box>
          </Stack>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="default" color="infgreen" size="md" radius="md" onClick={onClose}>
            취소
          </Button>
          <Button
            variant={allAgreed ? 'filled' : 'default'}
            color="infgreen"
            size="md"
            radius="md"
            disabled={!allAgreed}
            onClick={onAgree}
          >
            동의하고 시작하기
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
}

const styles = {
  consentBox: css({
    border: '1px solid #DEE2E6',
    borderRadius: 16,
  }),
};
